import React, { useEffect, useState } from 'react';
import {
  Plus, Eye, Users, DollarSign, Settings, TrendingUp,
  LogOut, User, ChevronRight, MapPin, FileText, X, Sparkles,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { host } from './script/variables';
import './style/PartnersHome.css';
import EmployeeManagement from './partner/EmployeeManagement';
import PartnerSettings    from './partner/PartnerSettings';
import { PlanBlockedDialog, PlanBanner } from './partner/PlanGate';
import { OPEN_PLANS_EVENT } from './partner/planGuard';
import Partner            from './Partner';
import PartnerDocuments   from './partner/PartnerDocuments';
import Analytics         from './partner/Analytics';
import Payroll from './Payroll';

// ─── Nav config ─────────────────────────────────────────────────────────────
// `mobileLabel` — short label that fits the bottom tab bar (≤ 8 chars ideal)
const NAV_ITEMS = [
  { icon: Settings,   label: 'Properties', mobileLabel: 'Properties', id: 'properties' },
  { icon: Users,      label: 'Employees',  mobileLabel: 'Staff',      id: 'employees'  },
  { icon: DollarSign, label: 'Payroll',    mobileLabel: 'Payroll',    id: 'payroll'    },
  { icon: TrendingUp, label: 'Analytics',  mobileLabel: 'Analytics',  id: 'analytics'  },
  // { icon: Settings,   label: 'Settings',   mobileLabel: 'Settings',   id: 'settings'   },
  { icon: FileText,   label: 'Documents',  mobileLabel: 'Docs',       id: 'documents'  },
];

// LottieFiles player CDN — swap this src for any Lottie JSON that fits
// your brand (search "confetti" or "celebration" at lottiefiles.com).
const LOTTIE_PLAYER_SRC = 'https://unpkg.com/@lottiefiles/lottie-player@2.0.4/dist/lottie-player.js';
const WELCOME_LOTTIE_SRC = 'https://assets9.lottiefiles.com/packages/lf20_obhph3sh.json';

// ─── Full-page "boom" welcome modal ────────────────────────────────────────
const WelcomeModal = ({ show, onClose }) => {
  const [playerReady, setPlayerReady] = useState(
    typeof window !== 'undefined' && !!window.customElements?.get('lottie-player')
  );

  useEffect(() => {
    if (playerReady || typeof document === 'undefined') return;
    const existing = document.querySelector('script[data-lottie-player]');
    if (existing) {
      existing.addEventListener('load', () => setPlayerReady(true));
      return;
    }
    const script = document.createElement('script');
    script.src = LOTTIE_PLAYER_SRC;
    script.setAttribute('data-lottie-player', 'true');
    script.onload = () => setPlayerReady(true);
    document.body.appendChild(script);
  }, [playerReady]);

  // lock background scroll while open
  useEffect(() => {
    if (!show) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [show]);

  if (!show) return null;

  return (
    <div className="wm-overlay" onClick={onClose}>
      <div
        className="wm-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="wm-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="wm-close" onClick={onClose} aria-label="Dismiss welcome message">
          <X size={18} />
        </button>

        <div className="wm-lottie-wrap">
          {playerReady ? (
            // eslint-disable-next-line react/no-unknown-property
            <lottie-player
              src={WELCOME_LOTTIE_SRC}
              background="transparent"
              speed="1"
              autoplay
              loop
              style={{ width: '180px', height: '180px' }}
            />
          ) : (
            <div className="wm-lottie-fallback">🎉</div>
          )}
        </div>

        <p className="wm-eyebrow">You're in</p>
        <h1 id="wm-title" className="wm-title">Welcome aboard, Partner</h1>
        <p className="wm-text">
          Thanks for joining the Restro Pilots family. Let's get your first
          property listed, staffed, and taking orders.
        </p>

        <div className="wm-actions">
          <button className="wm-cta" onClick={onClose}>
            <Sparkles size={16} /> Let's get started
          </button>
          <button className="wm-secondary" onClick={onClose}>
            I'll look around first
          </button>
        </div>
      </div>
    </div>
  );
};

const PartnerHome = ({ isLoggedin, setIsLoggedin, lastLoginTime }) => {
  const [myProperties, setMyProperties] = useState([]);
  const [activeNav, setActiveNav]       = useState(() => (new URLSearchParams(window.location.search).get('open') === 'plans' ? 'settings' : 'properties'));
  const [settingsTab, setSettingsTab]   = useState(() => (new URLSearchParams(window.location.search).get('open') === 'plans' ? 'plans' : undefined));

  // "See plans" from the upgrade dialog / banner → Settings → Plans & Billing
  useEffect(() => {
    const open = () => { setActiveNav('settings'); setSettingsTab('plans'); };
    window.addEventListener(OPEN_PLANS_EVENT, open);
    return () => window.removeEventListener(OPEN_PLANS_EVENT, open);
  }, []);
  const [loading, setLoading]           = useState(true);
  const [showWelcome, setShowWelcome]   = useState(!lastLoginTime);
  const navigate = useNavigate();

  const fetchProperties = async () => {
    try {
      const token = localStorage.getItem('partnertoken');
      
      const res = await fetch(`${host}/partner/fetchproperties`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Authorization: token },
      });
      if (res.ok) setMyProperties(await res.json());
    } catch { /* silent */ }
    finally { setLoading(false); }
  };
const handleLogout = async () => {
  // try {
  //   await fetch(`${host}/user/logoutPartner`, {
  //     method: 'POST',
  //     credentials: 'include',
  //     headers: { Authorization: localStorage.getItem('partnertoken') },
  //   });
  // } catch {
  //   // logout should succeed locally even if the network call fails
  // }

  localStorage.removeItem('partnertoken');
  document.cookie.split(';').forEach(c => {
    document.cookie = c
      .replace(/^ +/, '')
      .replace(/=.*/, `=;expires=${new Date(0).toUTCString()};path=/`);
  });

  setIsLoggedin(false);
};
  useEffect(() => { fetchProperties(); }, []);

  const totalActive = myProperties.reduce((a, p) => a + (p.onlineCount || 0), 0);

  return (
    <div className="ph-root">

      {/* ── Full-page "boom" welcome modal (first login only) ── */}
      {/* <WelcomeModal show={showWelcome} onClose={() => setShowWelcome(false)} /> */}

      {/* ══════════════════════════════════════════
          DESKTOP SIDEBAR
      ══════════════════════════════════════════ */}
      <aside className="ph-sidebar">
        <div className="ph-brand">
           <div className="p-brand">
              <img
                src="/production_logo/RestroPilots_Primary_Logo.svg"
                alt="RestroPilots"
                className="p-brand-logo"
              />
            </div>
        </div>

        <nav className="ph-nav">
          <p className="ph-nav-section-label">Management</p>
          {NAV_ITEMS.map(({ icon: Icon, label, id }) => (
            <button
              key={id}
              className={`ph-nav-item ${activeNav === id ? 'active' : ''}`}
              onClick={() => setActiveNav(id)}
            >
              <span className="ph-nav-icon"><Icon size={17} /></span>
              <span>{label}</span>
              {activeNav === id && <ChevronRight size={14} className="ph-nav-arrow" />}
            </button>
          ))}
        </nav>

        <div className="ph-sidebar-footer">
          <button className="ph-user-btn">
            <span className="ph-avatar"><User size={15} /></span>
            <span className="ph-user-label">


<button
              key={'settings'}
              className={`ph-nav-item ${activeNav === 'settings' ? 'active' : ''}`}
              onClick={() => setActiveNav('settings')}
            >
             
              <span>{'My Profile'}</span>
              {activeNav === 'settings' && <ChevronRight size={14} className="ph-nav-arrow" />}
            </button>

            </span>
          </button>
          <button className="ph-logout-btn" onClick={handleLogout}>
            <LogOut size={15} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* ══════════════════════════════════════════
          MAIN CONTENT
      ══════════════════════════════════════════ */}
      <main className="ph-main">
        <PlanBanner />
        <PlanBlockedDialog />

        {/* ─── PROPERTIES tab ─────────────────────── */}
        {activeNav === 'properties' && (
          <>
            <header className="ph-topbar">
              <div>
                <p className="ph-topbar-sub">Good day, Partner 👋</p>
                <h1 className="ph-topbar-title">My Properties</h1>
              </div>
              <button className="ph-btn-primary" onClick={() => navigate('/addproperty')}>
                <Plus size={17} /> Add Property
              </button>
            </header>

            <section className="ph-stats-row">
              <div className="ph-stat-card">
                <div className="ph-stat-icon ph-stat-icon--rust"><Settings size={20} /></div>
                <div>
                  <p className="ph-stat-label">Total Properties</p>
                  <p className="ph-stat-value">{loading ? '—' : myProperties.length}</p>
                </div>
              </div>
              <div className="ph-stat-card">
                <div className="ph-stat-icon ph-stat-icon--green"><Users size={20} /></div>
                <div>
                  <p className="ph-stat-label">Total Employees</p>
                  <p className="ph-stat-value">—</p>
                </div>
              </div>
              <div className="ph-stat-card">
                <div className="ph-stat-icon ph-stat-icon--gold"><TrendingUp size={20} /></div>
                <div>
                  <p className="ph-stat-label">Orders Active Now</p>
                  <p className="ph-stat-value">{loading ? '—' : totalActive}</p>
                </div>
              </div>
            </section>

            <div className="ph-section-head">
              <h2 className="ph-section-title">All Properties</h2>
              <span className="ph-section-count">{myProperties.length} listed</span>
            </div>

            {loading ? (
              <div className="ph-loading">
                <div className="ph-spinner" />
                <p>Fetching your properties…</p>
              </div>
            ) : myProperties.length === 0 ? (
              <div className="ph-empty">
                <div className="ph-empty-icon">🏠</div>
                <h3 className="ph-empty-title">No Properties Yet</h3>
                <p className="ph-empty-sub">Start growing your portfolio by adding your first restaurant.</p>
                <button className="ph-btn-primary" onClick={() => navigate('/addproperty')}>
                  <Plus size={16} /> Add Your First Property
                </button>
              </div>
            ) : (
              <div className="ph-grid">
                {myProperties.map(property => (
                  <div key={property._id} className="ph-card">
                    <div className="ph-card-header">
                      <div className="ph-card-avatar">
                        {property.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="ph-card-meta">
                        <h3 className="ph-card-name">{property.name}</h3>
                        <p className="ph-card-status">
                          <span className={`ph-dot ${property.onlineCount > 0 ? 'ph-dot--live' : ''}`} />
                          {property.onlineCount > 0 ? `${property.onlineCount} orders live` : 'No active orders'}
                        </p>
                        {property.location && (
                          <p className="ph-card-location">
                            <MapPin size={11} /> {property.location.city || 'Location set'}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="ph-card-stats">
                      <div className="ph-card-stat">
                        <span className="ph-card-stat-label">Staff</span>
                        <span className="ph-card-stat-value">—</span>
                      </div>
                      <div className="ph-card-stat">
                        <span className="ph-card-stat-label">Payroll</span>
                        <span className="ph-card-stat-value">—</span>
                      </div>
                      <div className="ph-card-stat">
                        <span className="ph-card-stat-label">Category</span>
                        <span className="ph-card-stat-value">{property.category || '—'}</span>
                      </div>
                    </div>

                    <div className="ph-card-actions">
                      <button
                        className="ph-btn-action ph-btn-action--primary"
                        onClick={() => navigate(`/partner/propertyView/${property._id}`)}
                      >
                        <Eye size={15} /> View Details
                      </button>
                      <button
                        className="ph-btn-action ph-btn-action--ghost"
                        onClick={() => setActiveNav('employees')}
                      >
                        <Users size={15} /> Manage Staff
                      </button>
                    </div>
                  </div>
                ))}

                <button className="ph-card ph-card--add" onClick={() => navigate('/addproperty')}>
                  <div className="ph-card-add-inner">
                    <div className="ph-card-add-icon"><Plus size={28} /></div>
                    <p className="ph-card-add-label">Add New Property</p>
                  </div>
                </button>
              </div>
            )}
          </>
        )}

        {/* ─── EMPLOYEES tab ─────────────────────── */}
        {activeNav === 'employees' && (
          <EmployeeManagement properties={myProperties} />
        )}

        {/* ─── PAYROLL tab ──────────────────────── */}
        {activeNav === 'payroll' && (
          <Payroll properties={myProperties} />
        )}

        {/* ─── ANALYTICS tab ────────────────────── */}
        {activeNav === 'analytics' && <Analytics />}

        {/* ─── SETTINGS tab ─────────────────────── */}
        {activeNav === 'settings' && <PartnerSettings initialTab={settingsTab} />}

        {/* ─── DOCUMENTS tab ────────────────────── */}
        {activeNav === 'documents' && (
          <PartnerDocuments properties={myProperties} />
        )}

      </main>

      {/* ══════════════════════════════════════════
          MOBILE BOTTOM NAV BAR
          Rendered in the DOM always; CSS hides it
          on desktop (display:none above 768px).
      ══════════════════════════════════════════ */}
      <nav className="ph-bottom-nav" aria-label="Main navigation">
        <div className="ph-bottom-nav-inner">

          {/* Main nav tabs */}
          {NAV_ITEMS.map(({ icon: Icon, mobileLabel, id }) => (
            <button
              key={id}
              className={`ph-bn-item ${activeNav === id ? 'active' : ''}`}
              onClick={() => setActiveNav(id)}
              aria-label={mobileLabel}
            >
              <span className="ph-bn-icon">
                <Icon size={20} />
              </span>
              {mobileLabel}
            </button>
          ))}

          {/* Logout — always last */}
          <button
            className="ph-bn-item ph-bn-logout"
            onClick={handleLogout}
            aria-label="Logout"
          >
            <span className="ph-bn-icon">
              <LogOut size={20} />
            </span>
            Out
          </button>

        </div>
      </nav>

    </div>
  );
};

export default PartnerHome;